/**
 * segmentFillSurfaces.js — the DUAL-SURFACE derivers for a Segment fill
 * (EW-003, ISP-9999, flag REACT_APP_SEGMENT_ARC_FILLS).
 *
 * Gordon's dual-surface law (specs/.../SEGMENT-FILL-DUAL-SURFACE-SPEC.md): a
 * Segment fill is ONE definition that must render, consistently, on TWO surfaces:
 *
 *   1. the CLOSED wedge on the wheel — an SVG `<pattern>` referenced by the arc's
 *      `fill:url(#p<id>)` (createPie.js). Derived by `toWedgePatternProps`.
 *   2. the OPEN segment frame — a DOM element painted with CSS `background`.
 *      Derived by `toFrameStyle`.
 *
 * The fill MODEL (`{ type, color?, gradient?, pattern?, image?, gif? }`, defined
 * in ./segmentFill.js) is the single source of truth; these two pure functions
 * are the ONLY place the model is turned into surface-specific props, so the
 * wedge and the frame can never silently diverge.
 *
 * Pure: no React, no DOM, no I/O — the geometry math is unit-tested in isolation.
 *
 * Crop is NON-DESTRUCTIVE: `focus = { x, y (0..1), zoom }` is stored and applied
 * at render time; nothing is ever baked into a new asset.
 *
 * Wedge geometry (deck reference — segment-fills.elevation.mjs:222-233): the
 * image `<pattern>` uses `patternUnits="userSpaceOnUse"` with a SQUARE patch
 * that covers the whole disc, anchored at the wheel's LOCAL origin (0,0 = wheel
 * centre) by offsetting the pattern box by `-halfW / -halfH`. The deck's own
 * numbers (patch 410 against radius 162) are ~1.27x the diameter; we use
 * WEDGE_COVER_FACTOR = 1.27 so a fill reaches every wedge with slight overscan.
 *
 * GIF on the wedge: SVG `<image>` FREEZES animated GIFs in Chromium/WebKit
 * (EW-009), so the wedge always renders the POSTER frame; the open frame is the
 * surface that actually animates (see toFrameStyle + resolveGifPlayback).
 */

import {
  buildSegmentFillCss,
  resolveGifPlayback,
} from "./segmentFill";
import {
  generatePatternSvg,
  DEFAULT_FG,
  DEFAULT_BG,
} from "../../../../backgroundPatterns/patternGenerators";
import { svgToDataUri } from "../../../../backgroundPatterns/toBackgroundCss";
import { cssGradientToSpec } from "../../../../gradientBuilder/gradientCss";

/**
 * A generated pattern SVG can only become a wedge `<image>` href as a DATA-URI —
 * a raw "<svg …>" markup string is NOT a valid `xlink:href`, so the `<image>`
 * silently fails to load and the wedge paints nothing (falling through to the
 * flat segment colour). Reuse the SAME encoder toBackgroundCss uses for the
 * open-frame `url("data:…")` tile so both surfaces derive an identical data-URI
 * from one pattern definition. That encoder is now the SHARED primitive
 * `svgToDataUri` (backgroundPatterns/toBackgroundCss) — imported above, no local
 * copy, so a fix there fixes every pattern surface at once.
 */

/**
 * Pull the tile URL out of a stored pattern CSS `background` value, e.g.
 *   `url("data:image/svg+xml,…") repeat, #FDE68A`  →  `data:image/svg+xml,…`
 * Matches `url("…")`, `url('…')` or `url(…)`. Returns "" when the css carries no
 * `url()` tile (a plain gradient/color css is not a pattern tile). This is the
 * shape the LEGACY feed carries (segmentBgColor + currentBg=3) — it has NO
 * pattern id to regenerate from, so the tile must come from the css directly.
 */
const cssTileUrl = (css) => {
  if (typeof css !== "string") return "";
  // Exclude only the SAME quote that WRAPS the url — the inner data-URI legitimately
  // contains the OTHER quote (encodeSvg emits `data:…svg xmlns='…' width='40'…`, i.e.
  // single quotes, inside a double-quoted url("…")), so a `[^'"]` class would stop
  // at the first inner quote and truncate the tile.
  let m = css.match(/url\(\s*"([^"]*)"\s*\)/i); // url("…")
  if (m) return m[1].trim();
  m = css.match(/url\(\s*'([^']*)'\s*\)/i); // url('…')
  if (m) return m[1].trim();
  m = css.match(/url\(\s*([^)]+?)\s*\)/i); // url(…) unquoted
  return m ? m[1].trim() : "";
};

export const PATTERN_TILE_DEFAULT = 34;

/**
 * Best-effort tile size (px) for a pattern wedge: the explicit `scale`, else the
 * SVG tile's own `width` parsed out of the css data-URI, else the shared default.
 *
 * EXPORTED because renderer-v2 (the engine that actually paints the live View and
 * Edit Wheel surfaces) needs the SAME number for its wedge tile. Guard the shared
 * primitive rather than forking it — a second copy is how the two wedge surfaces
 * silently diverge, which is the exact failure this module exists to prevent.
 */
export const patternTileSize = (p) => {
  const s = Number(p && p.scale);
  if (Number.isFinite(s) && s > 0) return s;
  const css = p && p.css;
  if (typeof css === "string") {
    const m = css.match(/width=['"]?(\d+(?:\.\d+)?)/i);
    if (m) {
      const w = Number(m[1]);
      if (Number.isFinite(w) && w > 0) return w;
    }
  }
  return PATTERN_TILE_DEFAULT;
};

/**
 * Square-patch coverage relative to the wheel DIAMETER. The deck patch (410) over
 * the deck diameter (324 = 2x162) is ~1.27; a value >1 guarantees the sliced
 * image reaches the outer rim of every wedge with a small overscan margin.
 */
export const WEDGE_COVER_FACTOR = 1.27;

const clamp01 = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0.5;
  return Math.min(1, Math.max(0, x));
};

const clampZoom = (z) => {
  const x = Number(z);
  if (!Number.isFinite(x)) return 1;
  return Math.min(4, Math.max(1, x));
};

const normFocus = (focus) => {
  const f = focus || {};
  return { x: clamp01(f.x ?? 0.5), y: clamp01(f.y ?? 0.5), zoom: clampZoom(f.zoom) };
};

// The media node of an image/gif fill (image | gif share the same crop shape).
const mediaOf = (fill) => {
  if (!fill) return null;
  if (fill.type === "image") return fill.image || null;
  if (fill.type === "gif") return fill.gif || null;
  return null;
};

/* -------------------------------------------------------------------------- */
/* Surface 1 — the CLOSED wedge (SVG <pattern>)                                */
/* -------------------------------------------------------------------------- */

/**
 * Derive the SVG `<pattern>` (+ inner `<image>`) props for a fill on a wheel
 * wedge. Returns `null` when the fill needs no pattern (solid color / gradient,
 * or an image/gif with no source yet).
 *
 * @param {object} fill the fill model `{ type, ... }`
 * @param {{outerRadius:number}} wheelGeom wedge geometry (wheel outer radius, px)
 * @returns {null | {
 *   patternUnits:'userSpaceOnUse', x:number, y:number, width:number, height:number,
 *   href:string, preserveAspectRatio:string, tile:boolean,
 *   image?:{x:number,y:number,width:number,height:number}
 * }}
 */
export const toWedgePatternProps = (fill, wheelGeom) => {
  const f = fill || {};
  const type = f.type;

  // Solid color and gradient do not use an image/tile pattern on the wedge —
  // the arc paints a flat color or (future) a gradient def instead.
  if (type !== "image" && type !== "gif" && type !== "pattern") return null;

  // ---- Pattern tile: userSpaceOnUse tile anchored at the wheel origin -------
  if (type === "pattern") {
    const p = f.pattern || {};
    // Derive the tile IMAGE href. Two stored shapes must BOTH paint (they are
    // the SAME pattern arriving on two data paths):
    //   1. a procedural descriptor `{ id, fg, bg, scale }` — the V2 fill blob a
    //      fresh SegmentFillPanel pick writes. Regenerate the SVG and wrap it as
    //      a data-URI.
    //   2. a css-only `{ css }` — what the LEGACY feed reconstructs from
    //      `segmentBgColor` + `currentBg=3` (the shape the reloaded segment, the
    //      wheel VIEW, and the gallery/explore card all carry — none of which
    //      round-trips a pattern id). Extract the tile data-URI from the css.
    // Before ISP-9999 only shape (1) was handled, AND its href was RAW `<svg>`
    // markup (an invalid <image> href) — so EVERY pattern wedge fell through to
    // the flat `d.data.color`, rendering solid instead of the pattern.
    let href = "";
    if (p.id) {
      href = svgToDataUri(
        generatePatternSvg(p.id, {
          fg: p.fg || DEFAULT_FG,
          bg: p.bg || DEFAULT_BG,
          scale: p.scale,
          opacity: p.opacity,
        })
      );
    }
    if (!href) href = cssTileUrl(p.css);
    if (!href) return null;
    const scale = patternTileSize(p);
    return {
      patternUnits: "userSpaceOnUse",
      x: 0,
      y: 0,
      width: scale,
      height: scale,
      href,
      preserveAspectRatio: "xMidYMid slice",
      tile: true,
    };
  }

  // ---- Image / GIF: square patch centred on the wheel origin ---------------
  const media = mediaOf(f) || {};
  // GIF freezes inside SVG <image> (EW-009) -> render the POSTER frame on the wedge
  // (a real first-frame poster when the feed carries `gif.poster`, else the gif url,
  // which an SVG <image> paints as a static first frame on Chromium). `animateHref`
  // is the ACTUAL gif url the EW-009 hover overlay must animate — never the poster.
  const href = type === "gif" ? media.poster || media.url : media.url;
  if (!href) return null;
  const animateHref = type === "gif" ? media.url : undefined;

  const geom = wheelGeom || {};
  const outerRadius = Number(geom.outerRadius) > 0 ? Number(geom.outerRadius) : 100;
  const patch = outerRadius * 2 * WEDGE_COVER_FACTOR;

  const focus = normFocus(media.focus);
  const imageW = patch * focus.zoom;
  const imageH = patch * focus.zoom;
  // Position the (possibly enlarged) image within the patch so the focal point
  // sits at the patch centre. At focus 0.5 / zoom 1 the image fills the patch
  // exactly (offset 0). This is the non-destructive crop.
  const imageX = (patch - imageW) * focus.x;
  const imageY = (patch - imageH) * focus.y;

  return {
    patternUnits: "userSpaceOnUse",
    x: -patch / 2,
    y: -patch / 2,
    width: patch,
    height: patch,
    href,
    animateHref,
    preserveAspectRatio: "xMidYMid slice",
    tile: false,
    image: { x: imageX, y: imageY, width: imageW, height: imageH },
  };
};

/* -------------------------------------------------------------------------- */
/* Surface 1b — the CLOSED wedge, GRADIENT paint (SVG <linear/radialGradient>) */
/* -------------------------------------------------------------------------- */

const GRADIENT_KINDS = ["linear", "radial", "conic"];

/**
 * Resolve a structured gradient spec ({ kind, stops:[{offset,color}], angleDeg?,
 * position? }) from a fill MODEL, from any shape a gradient arrives in:
 *   · { type:'gradient', pattern? gradient:{ css } }    — a raw css gradient string
 *   · { type:'gradient', gradient:{ kind, stops, ... } } — already structured
 *   · { type:'pattern',  pattern:{ css } }              — the LEGACY per-segment
 *       gradient path (segmentBgColor + currentBg=3 → { type:'pattern', {css} },
 *       via legacyFieldsToFillModel). This is the exact format the API-seeded
 *       QA gradient used, and the format the live Wallpaper gradient sub-panel
 *       stores — so BOTH resolve here.
 * Returns null for a non-gradient fill (image / gif / color / procedural pattern).
 */
const gradientSpecOf = (fill) => {
  const f = fill || {};

  if (f.type === "gradient") {
    const g = f.gradient || {};
    if (typeof g.css === "string") return cssGradientToSpec(g.css);
    if (GRADIENT_KINDS.includes(g.kind) && Array.isArray(g.stops)) {
      const stops = g.stops
        .map((s) => {
          const color = s && typeof s.color === "string" ? s.color.trim() : "";
          if (!color) return null;
          let offset = Number(s.offset);
          if (!Number.isFinite(offset)) {
            const pos = Number(s.position); // tolerate a 0..100 percentage
            offset = Number.isFinite(pos) ? pos / 100 : 0;
          }
          return { offset: clamp01(offset), color };
        })
        .filter(Boolean);
      if (stops.length < 2) return null;
      const spec = { kind: g.kind, stops };
      if (Number.isFinite(Number(g.angleDeg))) spec.angleDeg = Number(g.angleDeg);
      if (g.position && Number.isFinite(+g.position.x) && Number.isFinite(+g.position.y)) {
        spec.position = { x: +g.position.x, y: +g.position.y };
      }
      return spec;
    }
    return null;
  }

  if (f.type === "pattern") {
    const css = f.pattern && f.pattern.css;
    // A procedural-pattern tile is `url("data:...") ...` (NOT a gradient) — skip
    // it; only a real gradient css (no url()) becomes a gradient paint.
    if (typeof css === "string" && !/url\(/i.test(css)) return cssGradientToSpec(css);
  }

  return null;
};

/**
 * Derive the SVG gradient props for a segment fill on the CLOSED wheel wedge.
 *
 * Parity with renderer-v2 fills.js (compare-original-vs-reimplementation): a
 * CSS/structured LINEAR or RADIAL gradient paints a REAL `<linear/radialGradient>`
 * (objectBoundingBox) def — the caller (createPie.updateImage) builds the def and
 * points the arc `fill` at `url(#id)`. SVG has no native conic primitive, so a
 * CONIC gradient degrades to its FIRST STOP as a solid (renderer-v2's own
 * fallbackColor under-paint; the renderer-v2 View surface paints the full conic
 * via a clipped foreignObject). Either way this NEVER returns the generic
 * DEFAULT_WEDGE_COLOR (#e1e1e1) that a gradient degraded to before.
 *
 * @param {object} fill the fill model `{ type, ... }`
 * @returns {null
 *   | { kind:'linear', stops:Array<{offset,color}>, x1,y1,x2,y2, solid:string }
 *   | { kind:'radial', stops:Array<{offset,color}>, center:{cx,cy}|null, solid:string }
 *   | { kind:'solid',  stops:Array<{offset,color}>, solid:string } }
 */
export const toWedgeGradientProps = (fill) => {
  const spec = gradientSpecOf(fill);
  if (!spec || !Array.isArray(spec.stops) || spec.stops.length < 2) return null;

  const stops = spec.stops.map((s) => ({ offset: clamp01(s.offset), color: s.color }));
  const solid = stops[0].color;

  if (spec.kind === "linear") {
    // Map a CSS angle (0deg = to top, clockwise) to objectBoundingBox gradient
    // endpoints — IDENTICAL math to renderer-v2 fills.js so both surfaces agree.
    const deg = Number.isFinite(Number(spec.angleDeg)) ? Number(spec.angleDeg) : 180;
    const angle = (deg * Math.PI) / 180;
    const dx = Math.sin(angle) / 2;
    const dy = -Math.cos(angle) / 2;
    return {
      kind: "linear",
      stops,
      x1: 0.5 - dx,
      y1: 0.5 - dy,
      x2: 0.5 + dx,
      y2: 0.5 + dy,
      solid,
    };
  }

  if (spec.kind === "radial") {
    const p = spec.position;
    const center =
      p && Number.isFinite(+p.x) && Number.isFinite(+p.y) ? { cx: +p.x, cy: +p.y } : null;
    return { kind: "radial", stops, center, solid };
  }

  // conic → first-stop solid (SVG has no native conic; renderer-v2 View paints it).
  return { kind: "solid", stops, solid };
};

/* -------------------------------------------------------------------------- */
/* Surface 2 — the OPEN frame (DOM background)                                 */
/* -------------------------------------------------------------------------- */

const PLACEHOLDER = "#EDE7FB";

/**
 * Derive the CSS background props for a fill on the OPEN segment frame. The frame
 * is a DOM box (resizable/movable in the segment-environments concept); the fill
 * adapts to its size via `mode` (cover | contain | tile) + focal + zoom.
 *
 * Returns a plain style object (camelCase CSS props) so it drops straight onto a
 * React `style=` or an inline style map. Never returns a broken `url()`.
 *
 * @param {object} fill the fill model `{ type, ... }`
 * @param {{width?:number,height?:number}} frameRect the open frame's box (px)
 * @param {{open?:boolean, reducedMotion?:boolean, center?:string}} [ctx]
 * @returns {object} CSS background props
 */
export const toFrameStyle = (fill, frameRect, ctx = {}) => {
  const f = fill || {};
  const type = f.type;

  if (type === "color") {
    return { backgroundColor: f.color || "#6D5EF6" };
  }

  if (type === "gradient") {
    // Reuse the canonical, secured gradient/conic builder — one source of truth.
    return { backgroundImage: buildSegmentFillCss(f, ctx) };
  }

  if (type === "pattern") {
    // buildSegmentFillCss returns `url("data:...")` for a pattern tile.
    return {
      backgroundImage: buildSegmentFillCss(f, ctx),
      backgroundRepeat: "repeat",
      backgroundSize: "auto",
    };
  }

  if (type === "image" || type === "gif") {
    const media = mediaOf(f) || {};
    let src;
    if (type === "gif") {
      // Open frame is the surface that ANIMATES — but reduced-motion holds poster.
      src = resolveGifPlayback(media, ctx).src;
    } else {
      src = media.url;
    }
    if (!src) return { backgroundColor: PLACEHOLDER };

    const focus = normFocus(media.focus);
    const mode = media.mode || "cover";

    if (mode === "tile") {
      return {
        backgroundImage: `url("${src}")`,
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
      };
    }

    let backgroundSize;
    if (mode === "contain") {
      backgroundSize = "contain";
    } else {
      // cover: zoom>1 scales the image up as a percentage of the box; zoom 1
      // keeps the plain `cover` keyword (adaptive to any frame size — the resize
      // law: it re-covers whatever box it is given).
      backgroundSize = focus.zoom > 1 ? `${Math.round(focus.zoom * 100)}%` : "cover";
    }

    return {
      backgroundImage: `url("${src}")`,
      backgroundSize,
      backgroundPosition: `${Math.round(focus.x * 100)}% ${Math.round(focus.y * 100)}%`,
      backgroundRepeat: "no-repeat",
    };
  }

  // Unknown type -> safe neutral, never a broken url().
  return { backgroundColor: f.color || PLACEHOLDER };
};

const segmentFillSurfaces = {
  toWedgePatternProps,
  toWedgeGradientProps,
  toFrameStyle,
  WEDGE_COVER_FACTOR,
};
export default segmentFillSurfaces;
